import React, { useState } from "react";
import { MapPin, Mail, Phone, Clock, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import { company } from "../mock";
import GoogleReviewBadge from "../components/GoogleReviewBadge";

export default function Contact() {
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Please fill required fields",
        description: "Name, email and message are required.",
      });
      return;
    }
    setSubmitting(true);
    // Mock: store to localStorage
    const list = JSON.parse(localStorage.getItem("gt_enquiries") || "[]");
    list.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem("gt_enquiries", JSON.stringify(list));
    setTimeout(() => {
      setSubmitting(false);
      setDone(true);
      toast({
        title: "Enquiry received",
        description: "Our team will get back to you shortly.",
      });
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
    }, 700);
  };

  return (
    <div>
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-grid bg-grid-fade opacity-50" />
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-sky-500/25 blur-3xl animate-float-slow" />
        <div className="absolute -bottom-32 right-0 h-80 w-80 rounded-full bg-cyan-400/20 blur-3xl animate-float-slower" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 lg:py-28">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 text-sky-300 font-semibold text-xs tracking-[0.25em] uppercase">
              <Send className="h-3.5 w-3.5" /> Contact us
            </div>
            <h1 className="font-display mt-4 text-5xl lg:text-7xl font-bold tracking-tight leading-[1.02]">
              Let's tailor a{" "}
              <span className="bg-gradient-to-r from-sky-300 to-cyan-300 bg-clip-text text-transparent">
                solution
              </span>{" "}
              for your business.
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-2xl">
              Tell us a little about your requirement and our team will come
              back with ideas, timelines and a tailored proposal.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-3">
            <div className="rounded-2xl border border-slate-200 p-8 shadow-sm">
              <h2 className="text-xl font-semibold text-slate-900">
                Send us an enquiry
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                We usually respond within one business day.
              </p>

              {done ? (
                <div className="mt-8 flex flex-col items-center text-center py-10">
                  <div className="h-14 w-14 rounded-full bg-sky-100 text-sky-700 flex items-center justify-center">
                    <CheckCircle2 className="h-8 w-8" />
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">
                    Thank you!
                  </h3>
                  <p className="mt-1 text-sm text-slate-500 max-w-sm">
                    Your message has been received. Our team will get back to
                    you shortly.
                  </p>
                  <Button
                    onClick={() => setDone(false)}
                    className="mt-6 bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    Send another message
                  </Button>
                </div>
              ) : (
                <form onSubmit={onSubmit} className="mt-6 grid gap-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="name">Full name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        placeholder="Your name"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={onChange}
                        placeholder="you@company.com"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="+91 …"
                        className="mt-1.5"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        name="company"
                        value={form.company}
                        onChange={onChange}
                        placeholder="Company name"
                        className="mt-1.5"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">How can we help? *</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={5}
                      placeholder="Tell us about your project or requirement…"
                      className="mt-1.5"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-sky-600 hover:bg-sky-700 text-white h-11 rounded-md w-full sm:w-auto"
                  >
                    {submitting ? "Sending…" : "Send enquiry"}
                    <Send className="ml-2 h-4 w-4" />
                  </Button>
                </form>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 space-y-6">
            <div className="rounded-2xl bg-slate-950 text-white p-8">
              <h3 className="text-lg font-semibold">Global Tech Solutions</h3>
              <div className="mt-5 space-y-5 text-sm text-slate-300">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-sky-400 font-semibold mb-1.5">Office</div>
                  <div className="flex gap-3">
                    <MapPin className="h-5 w-5 text-sky-400 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{company.officeAddress}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-semibold mb-1.5">Registered</div>
                  <div className="flex gap-3">
                    <MapPin className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                    <span className="leading-relaxed">{company.registeredAddress}</span>
                  </div>
                </div>
                <div className="flex gap-3 items-center pt-2 border-t border-white/10">
                  <Phone className="h-5 w-5 text-sky-400" />
                  <a href={`tel:${company.phone}`} className="hover:text-white">
                    {company.phone}
                  </a>
                </div>
                <div className="flex gap-3 items-center">
                  <Mail className="h-5 w-5 text-sky-400" />
                  <a href={`mailto:${company.email}`} className="hover:text-white">
                    {company.email}
                  </a>
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 p-8">
              <div className="flex items-center gap-2 text-slate-900 font-semibold">
                <Clock className="h-5 w-5 text-sky-600" />
                Working Hours
              </div>
              <ul className="mt-5 divide-y divide-slate-100 text-sm">
                {company.hours.map((h) => (
                  <li
                    key={h.day}
                    className="flex items-center justify-between py-2.5"
                  >
                    <span className="text-slate-600">{h.day}</span>
                    <span
                      className={`font-medium ${
                        h.time === "Closed"
                          ? "text-rose-600"
                          : "text-slate-900"
                      }`}
                    >
                      {h.time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <GoogleReviewBadge />
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="rounded-2xl overflow-hidden ring-1 ring-slate-200 shadow-sm">
            <iframe
              title="Global Tech Solutions Location"
              src="https://www.google.com/maps?q=Veerbhadra+Nagar+Baner+Pune+411045&output=embed"
              className="w-full h-[380px] border-0"
              loading="lazy"
            />
          </div>
        </div>
      </section>
    </div>
  );
}
